export const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

export const NEW_USER = "New User";
export const RETURN_USER = "Return User";

const FORM_TYPE_LABELS = { standard: "Standard_PTO Forms", custom: "Custom" };
export const formTypeLabel = (formType) => FORM_TYPE_LABELS[formType] || formType;

export const monthKeyOf = (dateValue) => {
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : `${date.getFullYear()}-${date.getMonth()}`;
};

export const rankByCount = (rows, key, limit) => {
    const counts = new Map();
    for (const row of rows) {
        const value = row[key];
        counts.set(value, (counts.get(value) || 0) + 1);
    }
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return limit ? ranked.slice(0, limit) : ranked;
};

export const buildTimeline = (rows) => {
    const monthsByYear = new Map();
    for (const row of rows) {
        const date = new Date(row.updatedAt);
        if (Number.isNaN(date.getTime())) continue;
        const year = date.getFullYear();
        if (!monthsByYear.has(year)) monthsByYear.set(year, new Set());
        monthsByYear.get(year).add(date.getMonth());
    }

    return [...monthsByYear.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([year, monthSet]) => ({
            year,
            quarters: [0, 1, 2, 3].map(quarter => ({
                label: `Qtr ${quarter + 1}`,
                months: [0, 1, 2]
                    .map(offset => quarter * 3 + offset)
                    .filter(monthIndex => monthSet.has(monthIndex))
                    .map(monthIndex => ({
                        index: monthIndex,
                        label: MONTH_NAMES[monthIndex],
                        key: `${year}-${monthIndex}`
                    }))
            })).filter(quarter => quarter.months.length > 0)
        }));
};


export const buildUserList = (rows) => {
    const byMail = new Map();
    for (const row of rows) {
        if (!byMail.has(row.mail)) byMail.set(row.mail, {mail: row.mail, name: row.name});
    }
    return [...byMail.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
};


export const resolvePeriodStart = (rows, selectedMonths) => {
    if (selectedMonths.length > 0) {
        const parsed = selectedMonths
            .map(key => key.split("-").map(Number))
            .map(([year, month]) => new Date(year, month, 1));
        return new Date(Math.min(...parsed.map(date => date.getTime())));
    }

    let latest = null;
    for (const row of rows) {
        const date = new Date(row.updatedAt);
        if (Number.isNaN(date.getTime())) continue;
        if (!latest || date > latest) latest = date;
    }
    return latest ? new Date(latest.getFullYear(), latest.getMonth(), 1) : new Date(0);
};


export const classifyUsers = (rows, periodStart) => {
    const firstFormAt = new Map();
    for (const row of rows) {
        const date = new Date(row.updatedAt);
        if (Number.isNaN(date.getTime())) continue;
        const earliest = firstFormAt.get(row.mail);
        if (!earliest || date < earliest) firstFormAt.set(row.mail, date);
    }

    const userTypes = new Map();
    for (const [mail, earliest] of firstFormAt.entries()) {
        userTypes.set(mail, earliest >= periodStart ? NEW_USER : RETURN_USER);
    }
    return userTypes;
};

export const applyFilters = (rows, {months, formTypes, users, userTypes}, userTypeByMail) =>
    rows.filter(row => {
        if (months.length && !months.includes(monthKeyOf(row.updatedAt))) return false;
        if (formTypes.length && !formTypes.includes(row.formType)) return false;
        if (users.length && !users.includes(row.mail)) return false;
        if (userTypes.length && !userTypes.includes(userTypeByMail.get(row.mail))) return false;
        return true;
    });

export const applyCrossFilter = (rows, crossFilter, userTypeByMail) => {
    if (!crossFilter) return rows;
    const {field, value} = crossFilter;
    if (field === "userType") return rows.filter(row => userTypeByMail.get(row.mail) === value);
    return rows.filter(row => row[field] === value);
};

export const buildReport = (rows, userTypeByMail) => {
    const activeMails = new Set(rows.map(row => row.mail));

    let newUsers = 0;
    let returningUsers = 0;
    for (const mail of activeMails) {
        if (userTypeByMail.get(mail) === NEW_USER) newUsers += 1;
        else returningUsers += 1;
    }

    return {
        formsGenerated: rows.length,
        activeUsers: activeMails.size,
        newUsers,
        returningUsers,
        highestVolumeUsers: rankByCount(rows, "mail", 10)
            .map(([mail, value]) => ({
                key: mail,
                label: rows.find(r => r.mail === mail)?.name || mail,
                value
            })),
        formTypeVolume: rankByCount(rows, "formType")
            .map(([label, value]) => ({key: label, label, value})),
        ptoFormsFrequency: rankByCount(rows.filter(row => row.formType === "standard"), "formCode", 10)
            .map(([label, value]) => ({key: label, label, value})),
        customFormsFrequency: rankByCount(rows.filter(row => row.formType === "custom"), "formCode", 10)
            .map(([label, value]) => ({key: label, label, value})),
        formNameCounts: rankByCount(rows, "formName")
            .map(([label, value]) => ({key: label, label, value})),
        userMix: [
            {key: NEW_USER, label: NEW_USER, value: newUsers},
            {key: RETURN_USER, label: RETURN_USER, value: returningUsers}
        ]
    };
};
